import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import { DateTime } from 'luxon';
import { EVENTS } from '@/lib/constants';
import { EventBus } from '@/lib/event-bus';
import { accessPolicyType, notificationType, userScopeType } from 'shared/constants.json';
import { computed, ref } from 'vue';
import {
  createEntityV2,
  deleteEntityV2,
  getNotifications,
  updateNotification,
  updateNotificationAccessPolicy,
} from '@/store/actions';
import { defaultRRule } from '@/lib/rrule';
import { children as notificationChildren, notificationList } from '@/api/query/notification';
import { notification as notificationConfig } from 'shared/api/query/configs.json';
import { useStore } from 'vuex';

export default function useUpdateSchedules() {
  const store = useStore();
  const schedules = computed(() => store.state.notification);

  const { loggedInUser } = useLoggedInUser();
  const { loggedInUserAccount } = useLoggedInUserAccount();

  const createLoading = ref(false);
  const createSchedule = () => {
    createLoading.value = true;
    return createEntityV2(
      store,
      {
        entity: {
          creator: { uid: loggedInUser.value.uid },
          title: '',
          icon: '',
          typ: notificationType.writeUpdate,
          schedule: defaultRRule(),
          recipients: {
            account: { uid: loggedInUserAccount.value.uid },
            children: [{
              children: [{
                op: 'and',
                scope: {
                  type: userScopeType.staticUsers,
                  selectedOptions: [],
                  staticUsers: [{ uid: loggedInUser.value.uid }],
                  users: [],
                  property: null,
                  timeRange: null,
                  numberRange: null,
                },
              }],
              op: 'and',
            }],
            op: 'and',
          },
          accessPolicy: {
            account: { uid: loggedInUserAccount.value.uid },
            accountAccess: accessPolicyType.disabled,
          },
        },
        model: notificationConfig.model,
        attributes: notificationChildren([DateTime.local().toISO()]),
      },
    ).then((response) => {
      if (response.status !== 201) {
        throw new Error('error creating schedule');
      }
      EventBus.$emit(EVENTS.CHECKIN.SCHEDULE_CREATED);
      return schedules.value.find((s) => s.uid === response.data.uid);
    }).finally(() => {
      createLoading.value = false;
    });
  };
  const schedulesLoading = ref(false);
  const fetchSchedules = () => {
    schedulesLoading.value = true;
    getNotifications(store, {
      queries: notificationList({
        type: notificationType.writeUpdate,
        timeSeries: [DateTime.local().toISO()],
      }),
    })
      .then((data) => {
        const scheduleIDs = data.map((e) => e.uid);
        return schedules.value.filter((s) => scheduleIDs.includes(s.uid));
      })
      .finally(() => {
        schedulesLoading.value = false;
      });
  };

  const reset = (schedule) => {
    store.commit('ENTITY_UPDATED', { entity: schedule, model: notificationConfig.model });
  };

  const deleteLoading = ref(false);
  const deleteSchedule = (notification) => {
    deleteLoading.value = true;
    return deleteEntityV2(
      store,
      {
        entity: notification,
        model: notificationConfig.model,
      },
    ).then((response) => {
      if (response.status !== 200) {
        throw new Error('error when deleting notification');
      }
      EventBus.$emit(EVENTS.CHECKIN.SCHEDULE_DELETED);
    }).finally(() => {
      deleteLoading.value = false;
    });
  };

  const activate = (schedule) => updateSchedule({ ...schedule, activatedAt: DateTime.local().toISO() });
  const pause = (schedule) => updateSchedule({ ...schedule, activatedAt: null });

  const updateLoading = ref(false);
  const updateSchedule = (schedule, timeSeries = [DateTime.local().toISO()]) => {
    updateLoading.value = true;
    return updateNotification(
      store,
      {
        entity: schedule,
        attributes: notificationChildren(timeSeries),
      },
    )
      .then((schedule) => schedules.value.find((s) => s.uid === schedule.uid))
      .finally(() => {
        updateLoading.value = false;
      });
  };

  const updateScheduleAccessPolicy = (accessPolicy, notification) => updateNotificationAccessPolicy(
    store,
    { entity: { ...notification, accessPolicy } },
  )
    .then((schedule) => schedules.value.find((s) => s.uid === schedule.uid))
    .finally(() => {
      updateLoading.value = false;
    });

  return {
    fetchSchedules,
    schedulesLoading,
    schedules,
    createSchedule,
    createLoading,

    updateSchedule,
    reset,
    updateScheduleAccessPolicy,
    pause,
    activate,
    updateLoading,
    deleteLoading,
    deleteSchedule,
  };
}
